import React, { useRef, useState } from 'react';

/**
 * MagneticButton — the signature "surprise" interaction.
 * The button (and its label) are pulled toward the cursor as it
 * approaches, then springs back on leave, with a tracking yellow glow.
 */
export function MagneticButton({
  children,
  strength = 0.4,
  variant = 'solid',
  ...rest
}) {
  const ref = useRef(null);
  const labelRef = useRef(null);
  const [glow, setGlow] = useState({ x: 50, y: 50, on: false });

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    el.style.transform = `translate(${dx * strength}px, ${dy * strength}px)`;
    if (labelRef.current) labelRef.current.style.transform = `translate(${dx * strength * 0.4}px, ${dy * strength * 0.4}px)`;
    setGlow({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100, on: true });
  };

  const reset = () => {
    const el = ref.current;
    if (el) el.style.transform = 'translate(0,0)';
    if (labelRef.current) labelRef.current.style.transform = 'translate(0,0)';
    setGlow((g) => ({ ...g, on: false }));
  };

  const isSolid = variant === 'solid';

  return (
    <button
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={reset}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem 2.25rem',
        fontFamily: 'var(--font-display)',
        fontSize: 'var(--text-md)',
        fontWeight: 'var(--weight-semibold)',
        letterSpacing: 'var(--tracking-tight)',
        lineHeight: 1,
        borderRadius: 'var(--radius-pill)',
        border: isSolid ? 'none' : 'var(--border-thick) solid var(--border-accent)',
        background: isSolid ? 'var(--accent)' : 'transparent',
        color: isSolid ? 'var(--accent-contrast)' : 'var(--text-primary)',
        cursor: 'pointer',
        overflow: 'hidden',
        transition: 'transform var(--dur-slow) var(--ease-magnet), box-shadow var(--dur-base) var(--ease-out)',
        boxShadow: glow.on ? 'var(--glow-lg)' : 'var(--glow-sm)',
        WebkitTapHighlightColor: 'transparent',
      }}
      {...rest}
    >
      <span aria-hidden="true" style={{
        position: 'absolute', inset: 0,
        opacity: glow.on ? 1 : 0,
        transition: 'opacity var(--dur-base) var(--ease-out)',
        background: `radial-gradient(120px circle at ${glow.x}% ${glow.y}%, ${isSolid ? 'rgba(255,255,255,0.35)' : 'var(--yellow-a32)'}, transparent 60%)`,
        pointerEvents: 'none' }} />
      <span ref={labelRef} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', transition: 'transform var(--dur-slow) var(--ease-magnet)' }}>
        {children}
      </span>
    </button>
  );
}

export default MagneticButton;
